import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
// import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { isBefore, parseISO, isAfter, differenceInSeconds } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation, useParams } from 'wouter';

import { useGamePage } from '../../hooks/usePage';

import { useContest } from '../../contexts/ContestContext';
import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import EnterPinCodeForm from '../forms/EnterPincodeForm';
import Game from '../games/Game';
import CompleteFormEntry from '../partials/CompleteFormEntry';
import TimerBox from '../partials/TimerBox';
import UserBanner from '../partials/UserBanner';

import styles from '../../../styles/pages/game.module.scss';

function GamePage() {
    const { page = null, game, participation, reload, refetch, clearGamePage } = useGamePage();
    const [, navigate] = useLocation();
    // const queryClient = useQueryClient();
    const { participation: participationParam = null, question: questionSlug = null } =
        useParams<GamePageParams>();
    const url = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);

    const { access = null } = useContest();
    const labels = useLabels();
    const user = useUser();

    const { stats = {}, draws = null, profileCompleted = true, unusedPincode = null } = user || {};
    const { score = null } = stats || {};
    const { profile = null, requiresPincode = false } = access || {};
    const { required: profileRequired = false } = profile || {};

    const {
        id: gameId,
        type = null,
        maxPerDay = null,
        maxPerWeek = null,
        canParticipateAt = null,
        requiresPincode: gameRequiresPincode = false,
        theme: gameTheme = null,
    } = game || {};

    const { gameEnterPincodeTitle = null } = labels;

    const {
        enterPincodeBeforePlay = true,
        showUserScore = false,
        fullscreen = false,
    } = gameTheme || {};

    const {
        id: participationId = null,
        ended = false,
        endedAt: participationEndedAt = null,
        pincode: participationPincode = null,
    } = participation || {};

    useEffect(() => {
        if (participationParam === null && participationId !== null) {
            navigate(
                url(
                    questionSlug !== null ? 'participation.question' : 'participation',
                    questionSlug !== null
                        ? {
                              participation: participationId,
                              question: questionSlug,
                          }
                        : {
                              participation: participationId,
                          },
                ),
                {
                    replace: true,
                },
            );
        }

        if (participationParam !== null) {
            clearGamePage();
        }
    }, [participationParam, participationId, clearGamePage]);

    const participationEnded =
        ended ||
        (participationEndedAt !== null &&
            differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0);

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const pincode = participationPincode || unusedPincode;
    const needsPincode =
        (requiresPincode || gameRequiresPincode) &&
        pincode === null &&
        !participationEnded &&
        enterPincodeBeforePlay;

    const canPlay = useMemo(
        () =>
            (canParticipateAtDate === null ||
                isBefore(canParticipateAtDate, new Date()) ||
                participation !== null) &&
            !needsPincode,
        [participation, canParticipateAtDate, needsPincode],
    );

    const showTimer = useMemo(
        () =>
            participation === null &&
            canParticipateAtDate !== null &&
            isAfter(canParticipateAtDate, new Date()),
        [participation, canParticipateAtDate],
    );

    const onTimerComplete = useCallback(() => {
        reload();
    }, [reload]);

    const onPincodeSuccess = useCallback(() => {
        refetch();
    }, [refetch]);

    if (!profileCompleted && profileRequired) {
        return <Redirect to={`${url('register')}?next=${encodeURIComponent(page.url)}`} />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.fullscreen]: fullscreen,
                    [styles.cantPlay]: !canPlay,
                },
                `page-game-${type}`,
            ])}
        >
            {needsPincode && !showTimer ? (
                <div className={styles.pincodeContainer}>
                    <EnterPinCodeForm
                        participation={participation}
                        title={gameEnterPincodeTitle}
                        onSuccess={onPincodeSuccess}
                        className={styles.pincode}
                    />
                </div>
            ) : null}
            {participationEnded ? <CompleteFormEntry className={styles.completeFormEntry} /> : null}
            {showTimer ? (
                <div className={styles.timerContainer}>
                    <TimerBox
                        className={styles.timerBox}
                        maxPerDay={maxPerDay}
                        maxPerWeek={maxPerWeek}
                        started
                        date={canParticipateAtDate}
                        onEnded={onTimerComplete}
                        timerClassName={styles.timer}
                    />
                    {draws !== null && (draws || []).length > 0 ? (
                        <div className={styles.buttons}>
                            <StyledButton colored className={styles.button} href={url('account')}>
                                <FormattedMessage
                                    defaultMessage="See my chances"
                                    description="Button label"
                                />
                            </StyledButton>
                        </div>
                    ) : null}
                </div>
            ) : null}
            <div className={styles.game}>
                <Game game={game} participation={participation} canPlay={canPlay} reload={reload} />
            </div>
            {user !== null && showUserScore ? (
                <UserBanner
                    user={user}
                    points={score || null}
                    prefix={
                        <FormattedMessage
                            defaultMessage="Total score:"
                            description="Banner label"
                        />
                    }
                />
            ) : null}
        </div>
    );
}

export default GamePage;
